import { FC, useEffect, useState } from 'react';

import { format, isValid, parse } from 'date-fns';

import { SelectSingleEventHandler } from 'react-day-picker';

import { CalendarFillIcon } from '../../../assets';
import { EModalIds, closeModal, openModal } from '../../../lib';
import { Input } from '../input';
import { ALLOWED_DATE_CHARS_REGEX, DATE_FORMAT } from './config';
import { formatDateInput } from './lib';
import { SelectDateModal } from './select-date-modal';
import { IDatePickerInputProps } from './types';

export const DatePickerInput: FC<IDatePickerInputProps> = ({
  value,
  onSelect,
  modalId = EModalIds.SelectDateModal,
  calendarProps = {},
  placeholder,
  isInvalid,
  ...props
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [inputValue, setInputValue] = useState('');

  useEffect(() => {
    if (
      value &&
      isValid(value) &&
      value.getFullYear().toString().length === 4
    ) {
      setInputValue(format(value, DATE_FORMAT));
    }
  }, [value]);

  const selectDateHandler: SelectSingleEventHandler = (date) => {
    onSelect?.(date ?? null);
    closeModal(modalId);
  };

  const openSelectDateModal = () => {
    openModal({
      id: modalId,
      component: () => (
        <SelectDateModal
          {...calendarProps}
          selected={value}
          onSelect={selectDateHandler}
        />
      ),
    });
  };

  const focusToInputHandler = () => {
    setIsFocused(true);
  };

  const blurFromInputHandler = () => {
    setIsFocused(false);
  };

  const changeDateInputHandler = (newValue: string) => {
    if (
      newValue &&
      (!ALLOWED_DATE_CHARS_REGEX.test(newValue) ||
        newValue.length > DATE_FORMAT.length)
    ) {
      return;
    }

    const formattedValue = formatDateInput(newValue);
    setInputValue(formattedValue);

    const date = parse(formattedValue, DATE_FORMAT, new Date());

    if (isValid(date)) onSelect?.(date);
    else onSelect?.(null);
  };

  return (
    <div className="w-full">
      <Input
        value={inputValue}
        placeholder={placeholder}
        onValueChange={changeDateInputHandler}
        isInvalid={isInvalid}
        onFocus={focusToInputHandler}
        onBlur={blurFromInputHandler}
        beforeIcon={
          !inputValue &&
          isFocused && (
            <p className="absolute bottom-2 left-3 text-base-100 typography-16r">
              {DATE_FORMAT.toUpperCase()}
            </p>
          )
        }
        afterIcon={
          <button type="button" onClick={openSelectDateModal}>
            <CalendarFillIcon className="ml-auto h-[22px] w-[22px] shrink-0 cursor-pointer text-base-300" />
          </button>
        }
        {...props}
      />
      {!isInvalid && (
        <p className="mt-2 px-[14px] text-base-300 typography-13r">
          {value ? (
            <span className="text-brand-500">
              {value.toLocaleDateString('en-GB', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
              })}
            </span>
          ) : (
            `Format: ${DATE_FORMAT.toUpperCase()}`
          )}
        </p>
      )}
    </div>
  );
};
